import type { ReportQuery } from '../../services/report-query';
import type { DashboardWidgetDefinition, DashboardWidgetPlacement } from '../widget-and-graphs.models';
import { period } from '../widgets/query-period';

export interface DashboardRecordIdentity {
  separator: string;
  suffix: string;
  placementIds: 'static' | 'record';
  placementSeparator: string;
}

export interface DashboardRecordQueryScope {
  field: string;
  fieldsBySource?: Readonly<Record<string, string>>;
  mode: 'and' | 'replace';
  duration?: number;
}

export interface DefaultDashboardPlacement {
  /** Stored placement ID, or its stable prefix when record.placementIds is record. */
  id: string;
  widgetId: string;
  placement: { x: number; y: number; width: number; height: number };
  inputs?: Readonly<Record<string, unknown>>;
  bindContext?: boolean;
  when?: { flag: string; equals: boolean };
  filters?: readonly Record<string, unknown>[];
}

/** The same authored structure is used for app dashboards and opened-record dashboards. */
export interface DefaultDashboardDefinition {
  id: string;
  appId: string;
  name: string;
  record: DashboardRecordIdentity | null;
  queryScope: DashboardRecordQueryScope | null;
  placements: readonly DefaultDashboardPlacement[];
}

export interface DashboardContext {
  recordId?: string;
  inputs?: Readonly<Record<string, unknown>>;
  flags?: Readonly<Record<string, boolean>>;
}

export interface ResolvedDashboard {
  dashboardId: string;
  placements: DashboardWidgetPlacement[];
}

function recordId(context: DashboardContext): string {
  if (!context.recordId?.trim()) throw new Error('This dashboard requires an opened record ID.');
  return context.recordId;
}

export function resolveDashboardId(definition: DefaultDashboardDefinition, context: DashboardContext = {}): string {
  if (!definition.record) return definition.id;
  const identity = definition.record;
  return [definition.id, recordId(context), identity.suffix].join(identity.separator);
}

/** Reapply owner record constraints after composing a widget's query inputs. */
function recordQueryInputs(
  widget: DashboardWidgetDefinition,
  scope: DashboardRecordQueryScope,
  context: DashboardContext,
  filters: readonly Record<string, unknown>[],
): Record<string, unknown> {
  const openedId = recordId(context);
  const scoped = (query: ReportQuery): ReportQuery => {
    const field = scope.fieldsBySource?.[query.source] ?? scope.field;
    const predicate = { type: 'condition', field, operator: 'eq', value: openedId };
    const where = scope.mode === 'replace' && !filters.length ? predicate : {
      type: 'group', operator: 'and', children: [
        predicate, ...(scope.mode === 'and' && query.where ? [query.where] : []), ...filters,
      ],
    };
    return { ...query, ...(scope.duration === undefined ? {} : period(scope.duration)), where };
  };
  const inputs: Record<string, unknown> = {};
  for (const key of ['query', 'valueQuery', 'subtextQuery']) {
    if (widget.inputs[key]) inputs[key] = scoped(widget.inputs[key] as ReportQuery);
  }
  if (Array.isArray(widget.inputs['graphQueries'])) {
    inputs['graphQueries'] = widget.inputs['graphQueries'].map(series => ({ ...series, query: scoped(series.query) }));
  }
  if (Array.isArray(widget.inputs['metrics'])) {
    inputs['metrics'] = widget.inputs['metrics'].map(metric => ({ ...metric, query: scoped(metric.query) }));
  }
  return inputs;
}

/** Resolve instance identity, conditional placements and owner-scoped queries in one place. */
export function resolveDashboardPlacements(
  definition: DefaultDashboardDefinition,
  context: DashboardContext = {},
  widgets: readonly DashboardWidgetDefinition[] = [],
): DashboardWidgetPlacement[] {
  return definition.placements
    .filter(item => !item.when || context.flags?.[item.when.flag] === item.when.equals)
    .map(item => {
      const id = definition.record?.placementIds === 'record'
        ? [item.id, recordId(context)].join(definition.record.placementSeparator) : item.id;
      const inputs: Record<string, unknown> = {
        ...item.inputs, ...(item.bindContext ? context.inputs : {}),
      };
      if (definition.queryScope) {
        const widget = widgets.find(widget => widget.widgetId === item.widgetId);
        if (!widget) throw new Error(`Unknown widget in dashboard ${definition.id}: ${item.widgetId}`);
        Object.assign(inputs, recordQueryInputs(widget, definition.queryScope, context, item.filters ?? []));
      }
      return { id, widgetId: item.widgetId, ...item.placement,
        ...(Object.keys(inputs).length || item.bindContext ? { inputs } : {}),
      };
    });
}

export function resolveDashboard(
  definition: DefaultDashboardDefinition,
  context: DashboardContext = {},
  widgets: readonly DashboardWidgetDefinition[] = [],
): ResolvedDashboard {
  return { dashboardId: resolveDashboardId(definition, context), placements: resolveDashboardPlacements(definition, context, widgets) };
}
